import localForage from 'localforage';
import ApiServiceWrapper from '@/shared/api-request.service';
import { StoreInputParameters } from './idbStore.interface';
import { currentUserKey, isChrome } from '@/shared/msw-constants';

// INDEXEDDB STORE NAMES
export const PRODUCTS_STORE_NAME = 'products';
export const TENANTS_STORE_NAME = 'tenants';
export const USERGROUP_STORE_NAME = 'usergroups';
export const CSC_STORE_NAME = 'spog';

// let mode: string = '';

const getDbName = () => {
  const userObj = localStorage.getItem(currentUserKey);
  if (userObj) {
    if (JSON.parse(userObj)) {
      const userId = JSON.parse(userObj).userId;
      if (!isChrome) setUnclearedDatabaseIds(userId);
      return userId;
    }
  }
  return 'localForageDb';
};

const setUnclearedDatabaseIds = (userId: string) => {
  const ids = localStorage.getItem('UnclearedIdbs');
  if (ids) {
    const temp = JSON.parse(ids);
    if (temp.indexOf(userId) === -1) {
      temp.push(userId);
      localStorage.setItem('UnclearedIdbs', JSON.stringify(temp));
    }
  } else {
    localStorage.setItem('UnclearedIdbs', JSON.stringify([userId]));
  }
};

let idb: any;

try {
  const clientStore = localForage.createInstance({
    name: getDbName(),
  });
  clientStore.config({
    driver: [localForage.INDEXEDDB, localForage.LOCALSTORAGE, localForage.WEBSQL],
    version: 1.0,
    storeName: 'workspaceStore',
  });

  idb = (() => {
    async function generateKeyPair() {
      const keyObj: any = await clientStore.getItem('key');
      const counterObj: any = await clientStore.getItem('counter');
      if (!keyObj || !counterObj) {
        return window.crypto.subtle.generateKey(
          {
            name: 'AES-CTR',
            length: 256,
          },
          false,
          ['encrypt', 'decrypt'],
        )
          .then((key) => {
            clientStore.setItem('key', key);
            const counter = window.crypto.getRandomValues(new Uint8Array(16));
            clientStore.setItem('counter', counter);
          });
      }
    }

    async function encryptData(data: any) {
      await generateKeyPair();
      const keyObj: any = await clientStore.getItem('key');
      const counterObj: any = await clientStore.getItem('counter');
      const enc = new TextEncoder();
      const encoded = enc.encode(JSON.stringify(data));
      const encryptedData = await window.crypto.subtle.encrypt(
        {
          counter: counterObj,
          name: 'AES-CTR',
          length: 64,
        },
        keyObj,
        encoded,
      );
      return encryptedData;
    }

    async function decryptData(encryptedData: any) {
      const keyObj: any = await clientStore.getItem('key');
      const counterObj: any = await clientStore.getItem('counter');
      try {
        const decrypted = await window.crypto.subtle.decrypt(
          {
            counter: counterObj,
            name: 'AES-CTR',
            length: 64,
          },
          keyObj,
          encryptedData,
        );
        const dec = new TextDecoder();
        return JSON.parse(dec.decode(decrypted));
      } catch (e) {
        console.error(e);
      }
    }

    // async function getDataIncognito(endpoint: any, payload: any) {
    //   const { content } = await ApiServiceWrapper.post(endpoint ? endpoint : '', payload);
    //   return content;
    // }

    return {
      async saveData(type: string, data: any) {
        const compressedData = JSON.stringify(data);
        const encryptedData = await encryptData(compressedData);
        const response = await clientStore.setItem(type, encryptedData);
        return response;
      },

      async getData(params: StoreInputParameters, successMsgCodes: string[] = [], contentObject: string) {
        // let incognitoResponse: any;
        // if (!isChrome) {
        //   const canOpenDb: any = window.indexedDB.open('canOpenDb');
        //   canOpenDb.onerror = () => {
        //     mode = 'PRIVATE';
        //   };
        // }
        // if (mode !== '' && mode === 'PRIVATE') {
        //   incognitoResponse = await getDataIncognito(params.endpoint, params.payload);
        //   return incognitoResponse;
        // }
        const response = await clientStore.getItem(params.objectName);
        if (response) {
          const storesToRefresh: string[] | null = await clientStore.getItem('storesToRefresh');
          if (storesToRefresh && storesToRefresh.indexOf(params.objectName) > -1) {
            storesToRefresh.splice(storesToRefresh.indexOf(params.objectName), 1);
            clientStore.setItem('storesToRefresh', storesToRefresh);
            params.forceFetch = true;
          }
          // The data is available in store
          const decryptedData = await decryptData(response);
          const decompressedData = JSON.parse(decryptedData);
          if (!params.forceFetch && decryptedData) {
            return decompressedData;
          }
        }

        // Fetching the data from API
        let responseAPI: any = null;
        let data: any;
        if (params.httpMethod && params.httpMethod.toUpperCase() === 'GET') {
          responseAPI = await ApiServiceWrapper.get(params.endpoint ? params.endpoint : '');
        } else {
          responseAPI = await ApiServiceWrapper.post(params.endpoint ? params.endpoint : '', params.payload);
        }

        successMsgCodes.forEach((element) => {
          if (element === responseAPI.msgCode) {
            if (contentObject) {
              data = responseAPI[contentObject] ? responseAPI[contentObject] : null;
            } else {
              data = responseAPI ? responseAPI : null;
            }
          }
        });
        if (successMsgCodes.length === 0) {
          if (contentObject) {
            data = responseAPI[contentObject] ? responseAPI[contentObject] : null;
          } else {
            data = responseAPI ? responseAPI : null;
          }
        }
        if (params.objectName === PRODUCTS_STORE_NAME && params.payload.isPaged === 'NO') {
          data.isAllProductsLoaded = true;
        }
        data && this.saveData(params.objectName, data);
        return data;
      },

      async refreshStore(storeName: string) {
        const stores: string[] | null = await clientStore.getItem('storesToRefresh');
        if (!stores) {
          clientStore.setItem('storesToRefresh', [storeName]);
        } else {
          stores.push(storeName);
          clientStore.setItem('storesToRefresh', stores);
        }
      },

      removeItem(storeName: string) {
        return clientStore.removeItem(storeName);
      },

      dropDatabase() {
        indexedDB.deleteDatabase(getDbName());
      },

      clearData() {
        return clientStore.clear();
      },
    };
  })();
} catch (error) {
  console.warn('cannot create a localForage instance');
  console.error(error);
}

export default idb;
